import React, { useCallback, useRef, useEffect } from 'react';
import { MediaType } from 'src/system/media';
import { useMediaLogger } from './useMediaLogger';
import { useMediaValidation } from './useMediaValidation';
import { MediaMetadata } from './useMediaUpload';

interface UseIntegrationUploadProps {
  displayFilesLength: number;
  allContextMedia: any[];
  localFiles: any[];
  uploadMediaBatch: any;
  pendingUploadsRef: React.MutableRefObject<Set<number>>;
  category: string;
  onFilesReceived?: (files: File[]) => Promise<void>;
  onProcessingStart?: (fileCount: number) => void;
}

export const useIntegrationUpload = ({
  displayFilesLength,
  allContextMedia,
  localFiles,
  uploadMediaBatch,
  pendingUploadsRef,
  category,
  onFilesReceived,
  onProcessingStart,
}: UseIntegrationUploadProps) => {
  const logger = useMediaLogger('useIntegrationUpload');
  const { validateUploadSet } = useMediaValidation();

  const handleIntegrationUpload = useCallback(
    async (urls: string[]) => {
      try {
        logger.log(`Handling integration upload of ${urls.length} URLs`);

        // Signal that processing has started (shows dialog immediately)
        onProcessingStart?.(urls.length);

        // Fetch each URL and convert to File objects
        const fetchedFiles: File[] = await Promise.all(
          urls.map(async (url, idx) => {
            try {
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(
                  `Failed to fetch image: ${url} (status: ${response.status})`,
                );
              }
              const blob = await response.blob();
              const contentType = blob.type || 'image/jpeg';

              // Derive filename from URL or create a fallback name
              let filename = `remote_image_${Date.now()}_${idx}`;

              return new File([blob], filename, { type: contentType });
            } catch (err) {
              logger.warn('Error fetching URL:', url, err);
              throw err;
            }
          }),
        );
        // If onFilesReceived is provided, call it with the fetched files
        if (onFilesReceived) {
          await onFilesReceived(fetchedFiles);
          return; // Let onFilesReceived handle the rest of the upload process
        }

        // Continue with existing validation and upload logic if no onFilesReceived
        const acceptedFiles = await validateUploadSet({
          files: fetchedFiles,
          type: MediaType[category as keyof typeof MediaType],
          currentCount: displayFilesLength,
        });
        if (acceptedFiles.length === 0) {
          logger.warn('No files accepted after validation');
          return;
        }

        // Find the true maximum item number including any pending uploads
        const maxItemNumber = Math.max(
          ...allContextMedia.map((item: any) => item.MEDIA_ITEM_NUMBER),
          ...localFiles.map((item: any) => item.MEDIA_ITEM_NUMBER),
          0,
        );

        const baseDisplayOrder =
          Math.max(...localFiles.map((f) => f.MEDIA_DISPLAY_ORDER), -1) + 1;

        // Reset tracking for new batch
        pendingUploadsRef.current = new Set();

        const preparedFiles = await Promise.all(
          acceptedFiles.map(async (file, index) => {
            const itemNumber = maxItemNumber + index + 1;
            const metadata: MediaMetadata = {
              name: file.name,
              type: file.type,
              category: category,
              description: null,
              size: file.size,
              file: file,
              itemNumber,
              displayOrder: baseDisplayOrder + index,
            };
            pendingUploadsRef.current.add(itemNumber);
            // Log the addition of the file to the upload queue
            logger.log(
              `Added remote image to upload queue: ${file.name} (item #${itemNumber})`,
            );
            return metadata;
          }),
        );

        await uploadMediaBatch(preparedFiles);
      } catch (error) {
        logger.error('Error handling integration upload:', error);
      }
    },
    [
      displayFilesLength,
      allContextMedia,
      localFiles,
      uploadMediaBatch,
      validateUploadSet,
      logger,
      pendingUploadsRef,
      onFilesReceived,
      onProcessingStart,
    ],
  );

  return { handleIntegrationUpload };
};

interface UseUrlUploadProps {
  handleIntegrationUpload: (urls: string[]) => Promise<void>;
}

export const useUrlUpload = ({
  handleIntegrationUpload,
}: UseUrlUploadProps) => {
  // Create a ref to store the upload function
  const handleUrlUploadRef = useRef(handleIntegrationUpload);
  const isInitializedRef = useRef(false);

  // One-time initialization
  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }

    // Mark as initialized
    isInitializedRef.current = true;

    // Store initial handler
    handleUrlUploadRef.current = handleIntegrationUpload;

    // Cleanup function
    return () => {
      isInitializedRef.current = false;
      handleUrlUploadRef.current = async () => {
        console.warn('Upload handler called after unmount');
      };
    };
  }, []); // Empty deps array ensures one-time initialization

  // Return a stable function that uses the ref
  const handleUrlUpload = useCallback(async (urls: string[] | string) => {
    if (!isInitializedRef.current) {
      console.warn('Upload handler called before initialization');
      return;
    }

    // Normalise argument so we always work with an array
    const urlArray = Array.isArray(urls) ? urls : [urls];
    // Delegate the heavy lifting to the current integration upload handler
    await handleUrlUploadRef.current(urlArray);
  }, []); // No dependencies needed since we're using refs

  return { handleUrlUpload };
};
